<template>
  <UiCard class="tutorial-list">
    <div class="tutorial-list__header">
      <h3>Интерактивные руководства</h3>
      <p>Изучите возможности ЛабАктив с помощью пошаговых интерактивных руководств</p>
    </div>

    <div class="tutorial-list__filters">
      <button
        v-for="option in category_options"
        :key="option.value"
        type="button"
        :class="['tutorial-list__chip', { 'is-active': selectedCategory === option.value }]"
        @click="selectedCategory = option.value"
      >
        {{ option.label }}
      </button>
    </div>

    <div class="tutorial-items">
      <UiCard
        v-for="tutorial in filteredTutorials"
        :key="tutorial.id"
        :class="['tutorial-item', { 'tutorial-completed': isTutorialCompleted(tutorial.id) }]"
      >
        <div class="tutorial-item__row">
          <div class="tutorial-item__content">
            <div class="tutorial-item__title-row">
              <h4>{{ tutorial.name }}</h4>
              <UiBadge v-if="isTutorialCompleted(tutorial.id)" variant="accent">Пройден</UiBadge>
              <UiBadge v-else-if="!canStartTutorial(tutorial)" variant="neutral"
                >Заблокирован</UiBadge
              >
            </div>

            <p>{{ tutorial.description }}</p>

            <div class="tutorial-item__meta">
              <span>{{ tutorial.estimatedTime }} мин</span>
              <span>{{ tutorial.steps.length }} шагов</span>
              <UiBadge variant="neutral">{{ getCategoryName(tutorial.category) }}</UiBadge>
            </div>

            <div v-if="tutorial.prerequisites?.length" class="tutorial-item__prereq">
              <div class="tutorial-item__prereq-label">Требуется завершить:</div>
              <div class="tutorial-item__prereq-items">
                <UiBadge
                  v-for="prereq in tutorial.prerequisites"
                  :key="prereq"
                  :variant="isTutorialCompleted(prereq) ? 'accent' : 'neutral'"
                >
                  {{ getTutorialById(prereq)?.name || prereq }}
                </UiBadge>
              </div>
            </div>
          </div>

          <div class="tutorial-item__actions">
            <UiButton
              v-if="!isTutorialCompleted(tutorial.id) && canStartTutorial(tutorial)"
              @click="startTutorialFromList(tutorial.id)"
            >
              Начать
            </UiButton>

            <UiButton
              v-else-if="isTutorialCompleted(tutorial.id)"
              variant="secondary"
              @click="restartTutorial(tutorial.id)"
            >
              Повторить
            </UiButton>

            <UiButton v-else variant="secondary" :disabled="true">Заблокирован</UiButton>
          </div>
        </div>
      </UiCard>
    </div>

    <UiCard v-if="availableTutorials.length > 0" class="tutorial-list__progress">
      <div class="tutorial-list__progress-head">
        <span>Общий прогресс</span>
        <span>{{ completedTutorials.length }} / {{ availableTutorials.length }}</span>
      </div>
      <UiProgress variant="linear" :value="progressPercentage" :indeterminate="false" />
      <div class="tutorial-list__progress-value">{{ progressPercentage }}% завершено</div>
    </UiCard>

    <div class="tutorial-list__footer">
      <UiButton
        v-if="completedTutorials.length > 0"
        variant="secondary"
        @click="showResetDialog = true"
      >
        Сбросить прогресс
      </UiButton>
      <div class="tutorial-list__spacer" />
      <UiButton variant="secondary" @click="$emit('close')">Закрыть</UiButton>
    </div>

    <UiModal v-model="showResetDialog" title="Сброс прогресса" :max-width="400">
      <p>Вы уверены, что хотите сбросить весь прогресс обучения? Это действие нельзя отменить.</p>
      <template #footer>
        <UiButton variant="secondary" @click="showResetDialog = false">Отмена</UiButton>
        <UiButton @click="confirmReset">Сбросить</UiButton>
      </template>
    </UiModal>
  </UiCard>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTutorial } from '@/modules/tutorial/composables/useTutorial'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiModal from '@/shared/ui/UiModal.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'

const emit = defineEmits<{
  close: []
}>()

const {
  availableTutorials,
  completedTutorials,
  startTutorial,
  getTutorialById,
  isTutorialCompleted,
  resetTutorial,
  resetAllTutorials,
} = useTutorial()

const selectedCategory = ref('all')
const showResetDialog = ref(false)

const category_options = [
  { value: 'all', label: 'Все' },
  { value: 'onboarding', label: 'Знакомство' },
  { value: 'feature', label: 'Функции' },
]

const filteredTutorials = computed(() => {
  if (selectedCategory.value === 'all') {
    return availableTutorials.value
  }

  return availableTutorials.value.filter((tutorial) => tutorial.category === selectedCategory.value)
})

const progressPercentage = computed(() => {
  if (availableTutorials.value.length === 0) return 0
  return Math.round((completedTutorials.value.length / availableTutorials.value.length) * 100)
})

const canStartTutorial = (tutorial: { prerequisites?: string[] }) => {
  if (!tutorial.prerequisites) return true
  return tutorial.prerequisites.every((prereq: string) => isTutorialCompleted(prereq))
}

const getCategoryName = (category: string) => {
  switch (category) {
    case 'onboarding':
      return 'Знакомство'
    case 'feature':
      return 'Функции'
    case 'advanced':
      return 'Продвинутые'
    default:
      return category
  }
}

const startTutorialFromList = (tutorialId: string) => {
  startTutorial(tutorialId)
  emit('close')
}

const restartTutorial = (tutorialId: string) => {
  resetTutorial(tutorialId)
  startTutorialFromList(tutorialId)
}

const confirmReset = () => {
  resetAllTutorials()
  showResetDialog.value = false
}
</script>

<style scoped>
.tutorial-list {
  display: grid;
  gap: 12px;
}

.tutorial-list__header h3 {
  margin: 0 0 6px;
}

.tutorial-list__header p {
  margin: 0;
  color: #586578;
}

.tutorial-list__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tutorial-list__chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}

.tutorial-list__chip.is-active {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}

.tutorial-items {
  display: grid;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
}

.tutorial-item {
  border: 1px solid #e2e8f0;
}

.tutorial-completed {
  border-color: #0f172a;
  background: #f8fafc;
}

.tutorial-item__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.tutorial-item__content {
  min-width: 0;
  flex: 1;
}

.tutorial-item__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tutorial-item__title-row h4 {
  margin: 0;
}

.tutorial-item__content p {
  margin: 8px 0;
  color: #586578;
}

.tutorial-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: #586578;
  font-size: 12px;
}

.tutorial-item__prereq {
  margin-top: 8px;
}

.tutorial-item__prereq-label {
  font-size: 12px;
  color: #586578;
  margin-bottom: 4px;
}

.tutorial-item__prereq-items {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.tutorial-item__actions {
  display: flex;
  align-items: flex-start;
}

.tutorial-list__progress {
  display: grid;
  gap: 8px;
}

.tutorial-list__progress-head {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.tutorial-list__progress-value {
  text-align: center;
  font-size: 12px;
  color: #586578;
}

.tutorial-list__footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tutorial-list__spacer {
  flex: 1;
}

@media (max-width: 600px) {
  .tutorial-item__row {
    flex-direction: column;
  }
}
</style>
